import { labelAntrian } from '../../utils/antrian';
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, Chip,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Divider, Paper, AppBar,
  Toolbar, Container, useTheme, useMediaQuery, Snackbar,
  BottomNavigation, BottomNavigationAction, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Avatar, Badge,
  Fade, Slide, Zoom, TextField
} from '@mui/material';
import {
  Printer as PrinterIcon,
} from '@phosphor-icons/react';
import {
  Bluetooth as BluetoothIcon, Refresh as RefreshIcon,
  LogoutSharp as LogoutOutlined, Check as CheckIcon,
  HomeMaxOutlined as HomeOutlined, Restaurant, LocalDining,
  Timer, Fastfood, CheckCircle, ArrowForward, MoreVert,
  Visibility as VisibilityIcon, SwapHoriz as SwapHorizIcon,
  Print as PrintIcon, CampaignOutlined
} from '@mui/icons-material';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Logout } from '../../fitur/AuthSlice';
import { waktuCabang } from '../../utils/waktuCabang';
import { apiHost } from '../../utils/apiHost';
import { ambilKonfigStruk, barisTiketDariTransaksi } from '../../struk/strukWeb';
import { cetakBrowser } from '../../struk/cetakStruk';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const STATUS_DAPUR = {
  baru: {
    label: 'Pesanan Baru',
    color: '#E65100',
    bgColor: '#FFF3E0',
    textColor: '#E65100',
    icon: <Fastfood fontSize="small" />,
  },
  diproses: {
    label: 'Sedang Dimasak',
    color: '#1565C0',
    bgColor: '#E3F2FD',
    textColor: '#1565C0',
    icon: <Restaurant fontSize="small" />,
  },
  siapdiantar: {
    label: 'Siap Disajikan',
    color: '#2E7D32',
    bgColor: '#E8F5E9',
    textColor: '#2E7D32',
    icon: <CheckCircle fontSize="small" />,
  },
  diantar: {
    label: 'Sedang Diantar',
    color: '#6A1B9A',
    bgColor: '#F3E5F5',
    textColor: '#6A1B9A',
    icon: <LocalDining fontSize="small" />,
  },
  selesai: {
    label: 'Selesai',
    color: '#424242',
    bgColor: '#F5F5F5',
    textColor: '#424242',
    icon: <CheckCircle fontSize="small" />,
  }
};

const DataPesananDapur = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerInstance, setPrinterInstance] = useState(null);
  const [currentView, setCurrentView] = useState('home');
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredOrders, setFilteredOrders] = useState([]);

  const pollingRef = useRef(null);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${getApiBaseUrl()}/getDataOrderBaru`,
        { withCredentials: true }
      );
      if (response.data.status === 'success' && response.data.data.transaksi) {
        setOrders(response.data.data.transaksi);
        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal memuat pesanan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    pollingRef.current = setInterval(() => { fetchOrders(); }, 10000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  useEffect(() => {
    filterOrdersByDate();
  }, [orders, selectedDate]);

  const filterOrdersByDate = () => {
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    
    const filtered = orders.filter(order => {
      const orderDate = waktuCabang(order.createdAt);
      orderDate.setHours(0, 0, 0, 0);
      return orderDate.getTime() === selected.getTime();
    });
    
    setFilteredOrders(filtered);
  };

  const fetchOrderDetail = async (orderUuid) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(
        `${getApiBaseUrl()}/getTransaksiDetail/${orderUuid}`,
        { withCredentials: true }
      );
      if (response.data.data) setOrderDetails(response.data.data);
    } catch (err) {
      setNotification({ open: true, message: 'Gagal memuat detail pesanan', severity: 'error' });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder || !newStatus) return;
    try {
      setUpdatingId(selectedOrder.uuid);
      const response = await axios.put(
        `${getApiBaseUrl()}/updateStatusDapur/${selectedOrder.uuid}`,
        { status_dapur: newStatus },
        { withCredentials: true }
      );
      if (response.data.status === 'success') {
        setNotification({ open: true, message: `Status diubah: ${STATUS_DAPUR[newStatus].label}`, severity: 'success' });
        setStatusDialogOpen(false);
        setSelectedOrder(null);
        setNewStatus(null);
        await fetchOrders();
      }
    } catch (err) {
      setNotification({ open: true, message: err.response?.data?.message || 'Gagal mengupdate status', severity: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  // Tiket dapur: susunan & isinya diatur di menu Struk (tab Tiket Dapur); sama dengan yang dicetak aplikasi HP, tanpa harga.
  const handlePrintOrder = async (order) => {
    try {
      const transaksiResponse = await axios.get(`${getApiBaseUrl()}/getTransaksiDetail/${order.uuid}`, { withCredentials: true });
      const { cabangConfig } = await ambilKonfigStruk(axios, getApiBaseUrl(), user?.cabang?.uuid);
      const baris = barisTiketDariTransaksi({
        trx: transaksiResponse.data.data,
        jenis: 'dapur',
        cabangConfig,
        namaCabang: user?.cabang?.namacabang || '',
        kasir: user?.nama || user?.username || '',
        meja: order.Table?.name || '',
      });
      if (baris.jumlahItem === 0) {
        setNotification({ open: true, message: 'Tidak ada item untuk tiket dapur (menu pesanan ini dicetak di BAR)', severity: 'info' });
        return;
      }
      if (printerConnected && printerInstance) {
        await printerInstance.printBaris(baris);
        setNotification({ open: true, message: 'Pesanan berhasil dicetak', severity: 'success' });
      } else {
        cetakBrowser(baris); // printer Bluetooth belum terhubung: cetak lewat jendela cetak browser
      }
    } catch (err) {
      setNotification({ open: true, message: 'Gagal mencetak pesanan', severity: 'error' });
    }
  };

  const handleLogout = () => { dispatch(Logout()); navigate('/'); };

  const getNextStatus = (currentStatus) => {
    const statusFlow = ['baru', 'diproses', 'siapdiantar', 'diantar', 'selesai'];
    const currentIndex = statusFlow.indexOf(currentStatus);
    return currentIndex < statusFlow.length - 1 ? statusFlow[currentIndex + 1] : null;
  };

  const getCurrentStatus = (order) => order.Dapur?.status_dapur || 'baru';

  const prevBaruOrderIdsRef = useRef(null);
  const isFirstOrdersLoadRef = useRef(true);

  useEffect(() => {
    const baruIds = new Set(
      orders
        .filter((o) => (o.Dapur?.status_dapur || 'baru') === 'baru')
        .map((o) => o.uuid)
        .filter(Boolean)
    );

    if (isFirstOrdersLoadRef.current) {
      isFirstOrdersLoadRef.current = false;
      prevBaruOrderIdsRef.current = baruIds;
      return;
    }

    const prev = prevBaruOrderIdsRef.current || new Set();
    let hasNewBaru = false;
    baruIds.forEach((id) => {
      if (!prev.has(id)) hasNewBaru = true;
    });
    prevBaruOrderIdsRef.current = baruIds;

    if (!hasNewBaru) return;

    try {
      const src = `${process.env.PUBLIC_URL || ''}/notif.mp3`;
      const audio = new Audio(src);
      audio.volume = 0.85;
      audio.play().catch(() => {});
    } catch {
      /* autoplay may be blocked until user gesture */
    }
  }, [orders]);

  const getOrderStats = () => ({
    total: filteredOrders.length,
    baru: filteredOrders.filter(o => getCurrentStatus(o) === 'baru').length,
    diproses: filteredOrders.filter(o => getCurrentStatus(o) === 'diproses').length,
    siapdiantar: filteredOrders.filter(o => getCurrentStatus(o) === 'siapdiantar').length,
  });

  const stats = getOrderStats();

  const getTimeDiff = (orderTime) => {
    const diff = new Date() - new Date(orderTime);
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Baru saja';
    if (minutes < 60) return `${minutes} mnt`;
    return `${Math.floor(minutes / 60)} jam`;
  };

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#fff' }}>
        <CircularProgress size={40} sx={{ color: '#333' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#F7F8FA', pb: isMobile ? 10 : 3 }}>

      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: '#fff',
          borderBottom: '1px solid #E8E8E8',
          color: '#1a1a1a'
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Restaurant sx={{ color: '#1a1a1a', fontSize: 22, mr: 1 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1a1a1a', letterSpacing: '-0.3px' }}>
              Dapur
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            icon={<BluetoothIcon sx={{ fontSize: '14px !important' }} />}
            label={printerConnected ? "Printer OK" : "Printer"}
            size="small"
            onClick={() => setShowPrinterSettings(true)}
            sx={{
              cursor: 'pointer',
              mr: 1,
              fontWeight: 600,
              fontSize: 12,
              border: '1px solid #E0E0E0',
              background: printerConnected ? '#F1F8F1' : '#fff',
              color: printerConnected ? '#2E7D32' : '#666',
              '& .MuiChip-icon': { color: printerConnected ? '#2E7D32' : '#999' }
            }}
          />
          <Button
            startIcon={<CampaignOutlined fontSize="small" />}
            onClick={() => navigate('/antrian-dapur')}
            size="small"
            sx={{ mr: 1, fontWeight: 700, color: '#1a1a1a', border: '1px solid #E0E0E0' }}
          >
            Antrian
          </Button>
          <IconButton
            onClick={fetchOrders}
            size="small"
            sx={{ color: '#666', mr: 0.5, '&:hover': { background: '#F5F5F5' } }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          {!isMobile && (
            <IconButton onClick={handleLogout} size="small" sx={{ color: '#666', '&:hover': { background: '#F5F5F5' } }}>
              <LogoutOutlined fontSize="small" />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 2.5 }}>

        {/* Date Filter */}
        <Box sx={{ mb: 2.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: '#fff',
                fontSize: 13,
                fontWeight: 500,
                '&:hover fieldset': { borderColor: '#999' },
                '&:focus-within fieldset': { borderColor: '#1a1a1a' }
              },
              '& input': { cursor: 'pointer' }
            }}
          />
          <Typography sx={{ fontSize: 12, color: '#999' }}>
            {filteredOrders.length} pesanan ditemukan
          </Typography>
          {selectedDate !== new Date().toISOString().split('T')[0] && (
            <Button
              size="small"
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                fontWeight: 600,
                fontSize: 12,
                textTransform: 'none',
                borderColor: '#D0D0D0',
                color: '#666',
                px: 1.5,
                py: 0.5,
                '&:hover': { background: '#F5F5F5' }
              }}
            >
              Hari Ini
            </Button>
          )}
        </Box>

        {/* Stats Row */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {[
            { label: 'Pesanan Baru', value: stats.baru, dot: '#E65100' },
            { label: 'Sedang Dimasak', value: stats.diproses, dot: '#1565C0' },
            { label: 'Siap Disajikan', value: stats.siapdiantar, dot: '#2E7D32' },
            { label: 'Total Pesanan', value: stats.total, dot: '#424242' },
          ].map((stat, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: '#fff',
                  border: '1px solid #E8E8E8',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%', background: stat.dot,
                  flexShrink: 0, mt: 0.2
                }} />
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#888', mt: 0.3 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <Paper elevation={0} sx={{ border: '1px solid #E8E8E8', borderRadius: 2, overflow: 'hidden' }}>
          {/* Table Header */}
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>
              Daftar Pesanan
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#999' }}>
              {filteredOrders.length} pesanan aktif
            </Typography>
          </Box>

          {filteredOrders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Restaurant sx={{ fontSize: 48, color: '#DDD', mb: 1.5 }} />
              <Typography sx={{ color: '#aaa', fontSize: 14 }}>Tidak ada pesanan pada tanggal tersebut</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#FAFAFA' }}>
                    {['No', 'Customer', 'Meja / Metode', 'Status', 'Aksi'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#888',
                          py: 1.5,
                          px: 2,
                          borderBottom: '1px solid #F0F0F0',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map((order, index) => {
                    const currentStatus = getCurrentStatus(order);
                    const statusInfo = STATUS_DAPUR[currentStatus];
                    const nextStatus = getNextStatus(currentStatus);
                    const isCompleted = currentStatus === 'selesai';

                    return (
                      <TableRow
                        key={order.uuid}
                        sx={{
                          '&:hover': { background: '#FAFBFC' },
                          '&:last-child td': { borderBottom: 0 },
                          transition: 'background 0.15s'
                        }}
                      >
                        {/* No */}
                        <TableCell sx={{ px: 2, py: 1.8, color: '#bbb', fontSize: 12, width: 40 }}>
                          {index + 1}
                        </TableCell>

                        {/* Customer */}
                        <TableCell sx={{ px: 2, py: 1.8 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                            {order.namaCustomer || 'Walk-in'}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: '#bbb', fontFamily: 'monospace' }}>
                            {labelAntrian(order) ? `ANTRIAN ${labelAntrian(order)} · ` : ''}#{order.order_id || 'N/A'} - {getTimeDiff(order.createdAt)}
                          </Typography>
                        </TableCell>

                        {/* Meja */}
                        <TableCell sx={{ px: 2, py: 1.8 }}>
                          <Typography sx={{ fontSize: 13, color: '#444' }}>
                            {order.Table?.name || order.metode_pesanan || 'N/A'}
                          </Typography>
                        </TableCell>

                        {/* Item */}
                       

                        {/* Status */}
                        <TableCell sx={{ px: 2, py: 1.8 }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: 1.5,
                            background: statusInfo.bgColor,
                            color: statusInfo.textColor,
                            fontSize: 11,
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                          }}>
                            {React.cloneElement(statusInfo.icon, { style: { fontSize: 12 } })}
                            {statusInfo.label}
                          </Box>
                        </TableCell>

                        {/* Aksi */}
                        <TableCell sx={{ px: 2, py: 1.8 }}>
                          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                            {/* Detail */}
                            <Tooltip title="Lihat Detail" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedOrderForDetail(order);
                                  setDetailDialogOpen(true);
                                  fetchOrderDetail(order.uuid);
                                }}
                                sx={{
                                  color: '#555',
                                  border: '1px solid #E8E8E8',
                                  borderRadius: 1.5,
                                  p: 0.7,
                                  '&:hover': { background: '#F5F5F5', borderColor: '#ccc' }
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>

                            {/* Print */}
                            <Tooltip title="Print" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handlePrintOrder(order)}
                                sx={{
                                  color: '#555',
                                  border: '1px solid #E8E8E8',
                                  borderRadius: 1.5,
                                  p: 0.7,
                                  '&:hover': { background: '#F5F5F5', borderColor: '#ccc' }
                                }}
                              >
                                <PrintIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>

                            {/* Update Status */}
                            {!isCompleted && nextStatus && (
                              <Tooltip title={`Lanjut: ${STATUS_DAPUR[nextStatus].label}`} arrow>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  endIcon={updatingId === order.uuid
                                    ? <CircularProgress size={11} color="inherit" />
                                    : <ArrowForward sx={{ fontSize: '13px !important' }} />}
                                  onClick={() => {
                                    setSelectedOrder(order);
                                    setNewStatus(nextStatus);
                                    setStatusDialogOpen(true);
                                  }}
                                  disabled={updatingId === order.uuid}
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: '#1a1a1a',
                                    borderColor: '#D0D0D0',
                                    borderRadius: 1.5,
                                    px: 1.2,
                                    py: 0.5,
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none',
                                    '&:hover': { borderColor: '#999', background: '#FAFAFA' }
                                  }}
                                >
                                  {STATUS_DAPUR[nextStatus].label}
                                </Button>
                              </Tooltip>
                            )}

                            {isCompleted && (
                              <Chip
                                label="Selesai"
                                size="small"
                                sx={{ fontSize: 11, color: '#aaa', background: '#F5F5F5', border: 'none', height: 24 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => { setDetailDialogOpen(false); setOrderDetails(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #E8E8E8', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' } }}
      >
        <DialogTitle sx={{ px: 3, py: 2.5, borderBottom: '1px solid #F0F0F0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>Detail Pesanan</Typography>
          {selectedOrderForDetail && (
            <Typography sx={{ fontSize: 11, color: '#bbb', fontFamily: 'monospace', mt: 0.3 }}>
              #{selectedOrderForDetail?.uuid.substring(0, 13).toUpperCase()}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          {loadingDetail ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#ccc' }} />
            </Box>
          ) : selectedOrderForDetail && (
            <>
              {/* Info Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                {[
                  { label: 'Customer', value: selectedOrderForDetail.namaCustomer },
                  { label: 'Meja', value: selectedOrderForDetail.Table?.name || 'N/A' },
                  { label: 'Status', value: STATUS_DAPUR[getCurrentStatus(selectedOrderForDetail)].label },
                  { label: 'Kasir', value: selectedOrderForDetail.User?.username || '-' },
                  {label: 'Noted', value: selectedOrderForDetail.noted || '-'},
                  { label: 'Waktu Pesan', value: waktuCabang(selectedOrderForDetail.createdAt).toLocaleString('id-ID') },
                ].map(({ label, value }) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: 11, color: '#aaa', mb: 0.3, fontWeight: 500 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{value}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
                Item Pesanan
              </Typography>

              {selectedOrderForDetail.TransaksiDetails?.length > 0 ? (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0F0F0', borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#FAFAFA' }}>
                        {['No', 'Item', 'Qty'].map(h => (
                          <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: '#999', py: 1.2, px: 2 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrderForDetail.TransaksiDetails.map((item, index) => (
                        <TableRow key={index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ px: 2, py: 1.2, color: '#bbb', fontSize: 12, width: 30 }}>{index + 1}</TableCell>
                          <TableCell sx={{ px: 2, py: 1.2, fontSize: 13, color: '#333' }}>
                            {orderDetails?.TransaksiDetails?.[index]?.Barang?.namabarang || `Item (${item.barangUuid.substring(0, 8)})`}
                            {item.TransaksiDetailOpsis?.length > 0 && (
                              <Typography sx={{ fontSize: 11, color: '#666', mt: 0.3 }}>
                                {item.TransaksiDetailOpsis.map(o => o.opsinama).join(', ')}
                              </Typography>
                            )}
                            {item.catatan && (
                              <Typography sx={{ fontSize: 11, color: '#E65100', fontStyle: 'italic', mt: 0.3 }}>
                                Catatan: {item.catatan}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.2 }}>
                            <Box sx={{
                              display: 'inline-block',
                              background: '#F5F5F5',
                              color: '#555',
                              fontWeight: 600,
                              fontSize: 11,
                              px: 1, py: 0.3,
                              borderRadius: 1
                            }}>
                              {item.jumlahbarang}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 1.5 }}>Tidak ada item</Alert>
              )}

              {orderDetails?.total && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #F0F0F0' }}>
                  <Typography sx={{ fontSize: 13, color: '#666' }}>Total Harga</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>
                    Rp {orderDetails.total.toLocaleString('id-ID')}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #F0F0F0' }}>
          <Button
            onClick={() => { setDetailDialogOpen(false); setOrderDetails(null); }}
            variant="outlined"
            sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: 13, textTransform: 'none', borderColor: '#D0D0D0', color: '#333', '&:hover': { background: '#F5F5F5' } }}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Confirm Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #E8E8E8', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' } }}
      >
        <DialogTitle sx={{ px: 3, py: 2.5, borderBottom: '1px solid #F0F0F0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>Konfirmasi Status</Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {selectedOrder && newStatus && (
            <Box>
              <Typography sx={{ fontSize: 13, color: '#555', mb: 2 }}>
                Pesanan dari <strong style={{ color: '#1a1a1a' }}>{selectedOrder.namaCustomer}</strong> akan diubah statusnya menjadi:
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderRadius: 1.5,
                background: STATUS_DAPUR[newStatus].bgColor,
                border: `1px solid ${STATUS_DAPUR[newStatus].color}30`
              }}>
                {React.cloneElement(STATUS_DAPUR[newStatus].icon, { style: { color: STATUS_DAPUR[newStatus].textColor, fontSize: 16 } })}
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: STATUS_DAPUR[newStatus].textColor }}>
                  {STATUS_DAPUR[newStatus].label}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #F0F0F0', gap: 1 }}>
          <Button
            onClick={() => setStatusDialogOpen(false)}
            variant="outlined"
            sx={{ flex: 1, borderRadius: 1.5, fontWeight: 600, fontSize: 13, textTransform: 'none', borderColor: '#D0D0D0', color: '#555', '&:hover': { background: '#F5F5F5' } }}
          >
            Batal
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={!!updatingId}
            sx={{
              flex: 1, borderRadius: 1.5, fontWeight: 600, fontSize: 13, textTransform: 'none',
              background: '#1a1a1a', color: '#fff',
              boxShadow: 'none',
              '&:hover': { background: '#333', boxShadow: 'none' }
            }}
          >
            {updatingId ? <CircularProgress size={16} color="inherit" /> : 'Konfirmasi'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Printer Dialog */}
      <Dialog
        open={showPrinterSettings}
        onClose={() => setShowPrinterSettings(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #E8E8E8', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' } }}
      >
        <DialogTitle sx={{ px: 3, py: 2.5, borderBottom: '1px solid #F0F0F0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>
            {printerConnected ? 'Printer Terhubung' : 'Hubungkan Printer'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {!printerConnected ? (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 2, background: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                <BluetoothIcon sx={{ color: '#555', fontSize: 24 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#888', mb: 3 }}>
                Tekan tombol di bawah untuk menghubungkan printer Bluetooth
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<BluetoothIcon />}
                onClick={async () => {
                  try {
                    const device = await navigator.bluetooth.requestDevice({
                      filters: [{ services: ['000018f0-0000-1000-8000-00805f9b34fb'] }],
                      optionalServices: ['0000ffe0-0000-1000-8000-00805f9b34fb', '49535343-fe7d-4ae5-8fa9-9fafd205e455', 'e7810a71-73ae-499d-8c15-faa9aef0c3f2']
                    });
                    if (device) {
                      setPrinterInstance(device);
                      setPrinterConnected(true);
                      setNotification({ open: true, message: `Printer ${device.name} terhubung`, severity: 'success' });
                      setTimeout(() => setShowPrinterSettings(false), 1500);
                    }
                  } catch (err) {
                    setNotification({ open: true, message: 'Gagal menghubungkan printer', severity: 'error' });
                  }
                }}
                sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: 13, textTransform: 'none', borderColor: '#D0D0D0', color: '#333', py: 1.2, '&:hover': { background: '#F5F5F5' } }}
              >
                Cari Printer Bluetooth
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 2, background: '#F1F8F1', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                <CheckCircle sx={{ color: '#2E7D32', fontSize: 24 }} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
                {printerInstance?.name || 'Printer'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#aaa', mb: 3 }}>Terhubung</Typography>
              <Button
                variant="outlined"
                fullWidth
                color="error"
                onClick={() => {
                  setPrinterInstance(null);
                  setPrinterConnected(false);
                  setNotification({ open: true, message: 'Printer terputus', severity: 'info' });
                }}
                sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: 13, textTransform: 'none', py: 1.2 }}
              >
                Putus Koneksi
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom Nav Mobile */}
      {isMobile && (
        <BottomNavigation
          value={currentView}
          onChange={(event, newValue) => {
            if (newValue === 'logout') handleLogout();
            else if (newValue === 'refresh') fetchOrders();
            else if (newValue === 'printer') setShowPrinterSettings(true);
            else if (newValue === 'antrian') navigate('/antrian-dapur');
            else setCurrentView(newValue);
          }}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0, left: 0, right: 0,
            borderTop: '1px solid #EBEBEB',
            background: '#fff',
            '& .MuiBottomNavigationAction-root': { color: '#aaa', '&.Mui-selected': { color: '#1a1a1a' } },
            '& .MuiBottomNavigationAction-label': { fontSize: '11px !important' }
          }}
        >
          <BottomNavigationAction label="Home" value="home" icon={<HomeOutlined sx={{ fontSize: 20 }} />} />
          <BottomNavigationAction label="Refresh" value="refresh" icon={<RefreshIcon sx={{ fontSize: 20 }} />} />
          <BottomNavigationAction label="Antrian" value="antrian" icon={<CampaignOutlined sx={{ fontSize: 20 }} />} />
          <BottomNavigationAction label="Printer" value="printer" icon={<BluetoothIcon sx={{ fontSize: 20 }} />} />
          <BottomNavigationAction label="Logout" value="logout" icon={<LogoutOutlined sx={{ fontSize: 20 }} />} />
        </BottomNavigation>
      )}

      {/* Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DataPesananDapur;